import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/hooks/useLanguage";
import { EstablishmentProvider } from "@/hooks/useEstablishments";
import { AppStateProvider } from "@/contexts/AppStateContext";
import { RouteGuard } from "@/components/auth/RouteGuard";
import AdminProtectedRoute from "@/components/admin/AdminProtectedRoute";
import { queryClient } from "@/lib/queryClient";
import { FullscreenLoader } from "@/components/FullscreenLoader";

// Eager-load the landing page (critical for FCP)
import Index from "./pages/Index";

// Lazy-load all other pages
const NotFound = lazy(() => import("./pages/NotFound"));
const CaptureTunnel = lazy(() => import("./pages/CaptureTunnel").then(m => ({ default: m.CaptureTunnel })));
const DashboardLayout = lazy(() => import("./layouts/DashboardLayout"));
const DashboardHome = lazy(() => import("./pages/DashboardHome"));

const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const QRCodePage = lazy(() => import("./pages/QRCodePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ChoosePlanPage = lazy(() => import("./pages/ChoosePlanPage"));
const SuccessPage = lazy(() => import("./pages/SuccessPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const UpdatePasswordPage = lazy(() => import("./pages/UpdatePasswordPage"));
const SetupPage = lazy(() => import("./pages/SetupPage"));
const SetupWizard = lazy(() => import("./pages/SetupWizard"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const LegalMentionsPage = lazy(() => import("./pages/LegalMentionsPage"));
const DataDeletionPage = lazy(() => import("./pages/DataDeletionPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminHealthPage = lazy(() => import("./pages/AdminHealthPage"));
const EstablishmentsPage = lazy(() => import("./pages/EstablishmentsPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const LeaveReviewPage = lazy(() => import("./pages/LeaveReviewPage"));
const ReviewFunnelPage = lazy(() => import("./pages/ReviewFunnelPage"));
const AuthReconnectPage = lazy(() => import("./pages/AuthReconnectPage"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const FacebookOAuthCallback = lazy(() => import("./pages/FacebookOAuthCallback"));
const ProfileDiscoveryPage = lazy(() => import("./pages/ProfileDiscoveryPage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PricingStandalonePage = lazy(() => import("./pages/PricingStandalonePage"));
const GoogleDataUsagePage = lazy(() => import("./pages/GoogleDataUsagePage"));
const DiagramExportPage = lazy(() => import("./pages/DiagramExportPage"));
const WowPage = lazy(() => import("./pages/WowPage"));
const SeoPage = lazy(() => import("./pages/SeoPage"));
const DemoDashboard = lazy(() => import("./pages/DemoDashboard"));
const AppSnapshotProvider = lazy(() => import("@/hooks/useAppSnapshot").then(m => ({ default: m.AppSnapshotProvider })));

// Export queryClient for any module that still imports it from '@/App'
// (kept for backwards compatibility)
export { queryClient };

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AppStateProvider>
        <LanguageProvider>
          <EstablishmentProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              
              <Suspense fallback={<FullscreenLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/" element={<Index />} />
                  <Route path="/en" element={<Index />} />
                  <Route path="/capture" element={<CaptureTunnel />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/en/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/en/terms" element={<TermsOfServicePage />} />
                  <Route path="/legal" element={<LegalMentionsPage />} />
                  <Route path="/legal-notice" element={<LegalMentionsPage />} />
                  <Route path="/en/legal-notice" element={<LegalMentionsPage />} />
                  <Route path="/data-deletion" element={<DataDeletionPage />} />
                  <Route path="/en/data-deletion" element={<DataDeletionPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/en/contact" element={<ContactPage />} />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/en/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/pricing" element={<PricingStandalonePage />} />
                  <Route path="/en/pricing" element={<PricingStandalonePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/en/about" element={<AboutPage />} />
                  <Route path="/google-data-usage" element={<GoogleDataUsagePage />} />
                  <Route path="/en/google-data-usage" element={<GoogleDataUsagePage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/en/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/en/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />
                  <Route path="/leave-review/:businessId" element={<LeaveReviewPage />} />
                  <Route path="/review/:businessId" element={<ReviewFunnelPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/update-password" element={<UpdatePasswordPage />} />
                  <Route path="/auth-reconnect" element={<AuthReconnectPage />} />
                  <Route path="/auth/facebook/callback" element={<FacebookOAuthCallback />} />
                  <Route path="/diagram-export" element={<DiagramExportPage />} />
                  <Route path="/demo" element={<DemoDashboard />} />

                  {/* WOW page - post-setup, requires auth but not subscription */}
                  <Route
                    path="/wow"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck skipEmailVerification>
                        <Suspense fallback={<FullscreenLoader />}>
                          <AppSnapshotProvider>
                            <WowPage />
                          </AppSnapshotProvider>
                        </Suspense>
                      </RouteGuard>
                    }
                  />

                  {/* Auth routes - redirect if already authenticated */}
                  <Route
                    path="/login"
                    element={
                      <RouteGuard redirectIfAuthenticated>
                        <LoginPage />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="/signup"
                    element={
                      <RouteGuard redirectIfAuthenticated>
                        <SignupPage />
                      </RouteGuard>
                    }
                  />
                  
                  {/* Payment flow routes - require auth but skip subscription check */}
                  <Route
                    path="/choose-plan"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck skipEmailVerification>
                        <ChoosePlanPage />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="/success"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck skipEmailVerification>
                        <SuccessPage />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="/verify-email"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck skipEmailVerification>
                        <VerifyEmailPage />
                      </RouteGuard>
                    }
                  />

                  {/* Setup pages - requires auth but not subscription (establishment before plan) */}
                  <Route
                    path="/setup"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck>
                        <SetupPage />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="/profile-discovery"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck>
                        <ProfileDiscoveryPage />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="/setup-wizard"
                    element={
                      <RouteGuard requireAuth skipSubscriptionCheck>
                        <SetupWizard />
                      </RouteGuard>
                    }
                  />

                  {/* Admin routes */}
                  <Route
                    path="/admin"
                    element={
                      <AdminProtectedRoute>
                        <AdminDashboard />
                      </AdminProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/health"
                    element={
                      <AdminProtectedRoute>
                        <AdminHealthPage />
                      </AdminProtectedRoute>
                    }
                  />

                  {/* Protected dashboard routes - require auth + subscription */}
                  <Route
                    path="/dashboard"
                    element={
                      <RouteGuard requireAuth>
                        <DashboardLayout />
                      </RouteGuard>
                    }
                  >
                    <Route index element={<DashboardHome />} />
                    <Route path="reviews" element={<ReviewsPage />} />
                    
                    <Route path="qrcode" element={<QRCodePage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="establishments" element={<EstablishmentsPage />} />
                    <Route path="integrations" element={<IntegrationsPage />} />
                    <Route path="seo" element={<SeoPage />} />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TooltipProvider>
          </EstablishmentProvider>
        </LanguageProvider>
      </AppStateProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
